/**
 * The post chain, in order:
 *
 *   1. bright pass with a soft knee
 *   2. dual-filter bloom, N levels down and back up, weighted on the way up
 *   3. halation off a mid pyramid level, wider and warmer than the bloom
 *   4. persistence feedback at buffer resolution
 *   5. CRT composite at output resolution, tonemapped last
 *
 * Every constant in `CRT` and `BLOOM` is a live uniform reachable through
 * `set(key, value)`. Nothing in this chain requires a rebuild to retune, which
 * is the whole reason the visual-critic loop can converge on a look at all.
 */

import * as THREE from 'three';
import { BLOOM, CRT, QUALITY, SIM } from '@/core/config';
import type { QualityTier } from '@/core/config';
import { perf } from '@/core/perf';
import { Bloom } from './passes/Bloom';
import { CrtComposite } from './passes/CrtComposite';
import { Halation } from './passes/Halation';
import { Persistence } from './passes/Persistence';

/**
 * Everything tunable at runtime. The first two blocks mirror `BLOOM` and `CRT`
 * one for one; the third holds parameters this stack introduced and that
 * `core/config.ts` does not carry yet.
 */
export interface PostParams {
  levels: number;
  threshold: number;
  knee: number;
  intensity: number;
  weights: readonly number[];

  maskPitch: number;
  maskStrength: number;
  scanlineStrength: number;
  scanlineSharpness: number;
  scanlineMinPeriod: number;
  scanlineFloor: number;
  curvatureX: number;
  curvatureY: number;
  chromaticAberration: number;
  persistence: number;
  halationStrength: number;
  halationTint: readonly [number, number, number];
  vignette: number;
  vignettePower: number;
  vignetteDesat: number;
  edgeBlur: number;
  edgeBlurStart: number;
  edgeBlurPx: number;
  noise: number;
  overscan: number;
  glare: number;

  /** Tent width of the bloom upsample, in source texels. */
  bloomRadius: number;
  /** Tap spacing of the halation blur, in eighth-resolution texels. */
  halationRadius: number;
  /** Per-channel monomolecular time constant of the phosphor, in seconds. */
  phosphorTau: readonly [number, number, number];
  /** Per-channel bimolecular decay rate of the phosphor. */
  phosphorRate: readonly [number, number, number];
  /** Luminance under which the trail is cut to nothing. */
  persistenceFloor: number;
  /** Saturation applied to the retained trail. Above 1 pushes the hue out. */
  trailSaturation: number;
}

export function defaultParams(): PostParams {
  return {
    levels: BLOOM.levels,
    threshold: BLOOM.threshold,
    knee: BLOOM.knee,
    intensity: BLOOM.intensity,
    weights: BLOOM.weights.slice(),

    maskPitch: CRT.maskPitch,
    maskStrength: CRT.maskStrength,
    scanlineStrength: CRT.scanlineStrength,
    scanlineSharpness: CRT.scanlineSharpness,
    scanlineMinPeriod: CRT.scanlineMinPeriod,
    scanlineFloor: CRT.scanlineFloor,
    curvatureX: CRT.curvatureX,
    curvatureY: CRT.curvatureY,
    chromaticAberration: CRT.chromaticAberration,
    persistence: CRT.persistence,
    halationStrength: CRT.halationStrength,
    halationTint: [CRT.halationTint[0], CRT.halationTint[1], CRT.halationTint[2]],
    vignette: CRT.vignette,
    vignettePower: CRT.vignettePower,
    vignetteDesat: CRT.vignetteDesat,
    edgeBlur: CRT.edgeBlur,
    edgeBlurStart: CRT.edgeBlurStart,
    edgeBlurPx: CRT.edgeBlurPx,
    noise: CRT.noise,
    overscan: CRT.overscan,
    glare: CRT.glare,

    bloomRadius: 1.0,
    halationRadius: 2.0,
    phosphorTau: [CRT.phosphorTau[0], CRT.phosphorTau[1], CRT.phosphorTau[2]],
    phosphorRate: [CRT.phosphorRate[0], CRT.phosphorRate[1], CRT.phosphorRate[2]],
    persistenceFloor: CRT.persistenceFloor,
    trailSaturation: CRT.trailSaturation,
  };
}

export interface PostStackOptions {
  width: number;
  height: number;
  type: THREE.TextureDataType;
  quality: QualityTier;
}

export class PostStack {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly bloom: Bloom;
  private readonly halation: Halation;
  private readonly persistence: Persistence;
  private readonly composite: CrtComposite;
  /** Bound wherever a pass is switched off, so no sampler is ever left dangling. */
  private readonly black: THREE.DataTexture;

  private params: PostParams;
  private tier: QualityTier;
  private bufferW: number;
  private bufferH: number;
  /** Last governor generation folded into the uniforms. */
  private perfGeneration = perf.generation;
  /** Presentation time of the previous frame, for the persistence clock. */
  private lastPresent = -1;
  /** Whether the persistence pass ran last frame, so a gap can invalidate. */
  private persistedLast = false;
  private readonly motionQuery: MediaQueryList | null;

  constructor(renderer: THREE.WebGLRenderer, options: PostStackOptions) {
    this.renderer = renderer;
    this.bufferW = options.width;
    this.bufferH = options.height;
    this.tier = options.quality;
    this.params = defaultParams();

    this.bloom = new Bloom(options.width, options.height, options.type, BLOOM.levels);
    this.halation = new Halation(options.width, options.height, options.type);
    this.persistence = new Persistence(options.width, options.height, options.type);
    this.composite = new CrtComposite();

    this.black = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1);
    this.black.needsUpdate = true;

    // Read once and left live, the same way `HydraBackground` does it. A trail
    // that hangs on the glass for a second after the sprite has gone is motion
    // whatever else it is, so the preference has to reach this pass.
    this.motionQuery = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

    this.apply();
  }

  /** Live value of a tuning parameter. */
  get<K extends keyof PostParams>(key: K): PostParams[K] {
    return this.params[key];
  }

  /** Sets one tuning parameter and pushes the whole set back into the uniforms. */
  set<K extends keyof PostParams>(key: K, value: PostParams[K]): void {
    this.params[key] = value;
    this.apply();
  }

  /** Batch form. Cheaper than a `set` per key when the critic loop sweeps. */
  setAll(patch: Partial<PostParams>): void {
    Object.assign(this.params, patch);
    this.apply();
  }

  /** A copy of the current tuning, suitable for logging beside a screenshot. */
  snapshot(): PostParams {
    return {
      ...this.params,
      weights: this.params.weights.slice(),
      halationTint: [...this.params.halationTint] as [number, number, number],
      phosphorTau: [...this.params.phosphorTau] as [number, number, number],
      phosphorRate: [...this.params.phosphorRate] as [number, number, number],
    };
  }

  reset(): void {
    this.params = defaultParams();
    this.apply();
  }

  setQuality(tier: QualityTier): void {
    this.tier = tier;
    this.apply();
  }

  /**
   * Re-sizes every pass, and books the afterglow for a wipe.
   *
   * A persistence trail is the previous frame at the previous geometry; carried
   * across a buffer that has changed shape it is not a dimmer version of the
   * picture, it is a different picture being maxed against this one. Until the
   * rendered region became adaptive the buffer never changed size at all, so
   * none of this was ever exercised.
   *
   * `invalidate` and not `clearHistory`, and the difference is the whole bug.
   * Everything that calls `resize` — the resize observer, a DPR change, a tier
   * switch — runs outside any frame, and a clear issued there does not survive
   * to the next draw. Clearing eagerly therefore did nothing *and* marked the
   * buffer primed, which cancelled the deferred wipe that does work. Measured at
   * 405x870, where the HUD bands settle late enough that the last buffer resize
   * lands just before a capture batch: the tube read a mean luminance of exactly
   * 0 for the whole batch, and one `clearHistory` afterwards brought it back
   * permanently. Booking the wipe puts it inside the next `render`, where the
   * feedback loop already runs.
   */
  resize(bufferW: number, bufferH: number, outputW: number, outputH: number, scale: number): void {
    const moved = bufferW !== this.bufferW || bufferH !== this.bufferH;
    this.bufferW = bufferW;
    this.bufferH = bufferH;
    this.bloom.resize(bufferW, bufferH);
    this.halation.resize(bufferW, bufferH);
    this.persistence.resize(bufferW, bufferH);
    this.composite.setGeometry(outputW, outputH, bufferW, bufferH, scale);
    if (moved) this.persistence.invalidate();
  }

  /** Wipes the feedback buffer. Deterministic captures want this before frame 0. */
  clearHistory(): void {
    this.persistence.reset(this.renderer);
  }

  set time(value: number) {
    this.composite.time = value;
  }

  /** Runs the whole chain and leaves the result on the default framebuffer. */
  render(sceneTexture: THREE.Texture): void {
    // Bloom level count lives in a uniform-adjacent field rather than in the
    // per-frame path, so a rung change has to be folded back in here.
    if (this.perfGeneration !== perf.generation) {
      this.perfGeneration = perf.generation;
      this.apply();
    }

    const quality = QUALITY[this.tier];
    // Both gates are `and`, never `or`: the governor can switch a pass off that
    // the tier allows, and can never switch one on that the tier does not.
    const halationOn = quality.halation && perf.caps.halation && this.params.halationStrength > 0;
    const persistOn = quality.persistence && perf.caps.persistence && this.params.persistence > 0;

    this.bloom.renderDown(this.renderer, sceneTexture, this.bufferW, this.bufferH);

    // Read the pyramid before the upsample writes back into it, otherwise the
    // halation source is the bloom itself and the two effects compound.
    if (halationOn) this.halation.render(this.renderer, this.bloom.mip(2));

    this.bloom.renderUp(this.renderer);

    if (persistOn) {
      // Coming back from a rung that had the pass off, the accumulator holds
      // whatever was on screen when it was switched off. At the old three-frame
      // trail that was invisible; at a second it would be a ghost of somewhere
      // else in the round fading in on top of the fight.
      if (!this.persistedLast) this.persistence.invalidate();
      this.persistence.render(this.renderer, sceneTexture, this.decaySeconds());
    } else if (this.persistedLast) {
      // The rung just went off: free both accumulators rather than holding
      // ~7.4MB of half-float for a pass that renders nothing.
      this.persistence.release();
    }
    this.persistedLast = persistOn;

    this.composite.setTextures(
      sceneTexture,
      persistOn ? this.persistence.texture : this.black,
      this.bloom.texture,
      halationOn ? this.halation.texture : this.black,
    );
    this.composite.uniforms.uPersistMix.value = persistOn ? 1 : 0;
    this.composite.render(this.renderer);
  }

  /**
   * Seconds of phosphor decay to charge this frame.
   *
   * The presentation clock, not `performance.now()`, and the difference is the
   * whole reason captures still reproduce. `document.timeline.currentTime` only
   * moves when a frame is presented, so a synchronous `advanceFrames(n)` — which
   * is one task, not n frames — sees it frozen and every step gets the nominal
   * period. Under the animation loop it moves once per refresh, so a 144Hz
   * display charges 6.9ms where a 60Hz display charges 16.7ms and the trail
   * lasts the same wall-clock time on both. That is the bug this replaces: a
   * constant per-frame factor decayed twice as fast on a fast display and, under
   * the harness, once per *step* rather than per unit time.
   *
   * Capped at the nominal period rather than clamped to a window. Below 60Hz —
   * a stall, a background tab, the 400ms a capture waits between batches — the
   * cap is what keeps the answer the same every run. The trail then lasts longer
   * in wall time on a machine that cannot hold 60, which is the harmless
   * direction and matches the rest of the renderer's clock.
   */
  private decaySeconds(): number {
    const nominal = 1 / SIM.hz;
    const now = typeof document !== 'undefined' && typeof document.timeline?.currentTime === 'number'
      ? document.timeline.currentTime
      : -1;

    let dt = nominal;
    if (now >= 0) {
      const elapsed = now - this.lastPresent;
      if (this.lastPresent >= 0 && elapsed > 0) dt = Math.min(elapsed / 1000, nominal);
      this.lastPresent = now;
    }

    // A shorter trail rather than none: an eighth still reads as phosphor and
    // sits under the sprite instead of streaming out behind it.
    const scale = this.motionQuery?.matches === true
      ? CRT.reducedPersistence
      : this.params.persistence;
    return dt / Math.max(scale, 1e-3);
  }

  dispose(): void {
    this.bloom.dispose();
    this.halation.dispose();
    this.persistence.dispose();
    this.composite.dispose();
    this.black.dispose();
  }

  private apply(): void {
    const p = this.params;
    const quality = QUALITY[this.tier];

    this.bloom.levels = Math.max(
      1,
      Math.min(quality.bloomLevels, perf.caps.bloomLevelCap, Math.round(p.levels), BLOOM.levels),
    );
    this.bloom.setThreshold(p.threshold, p.knee);
    this.bloom.radius = p.bloomRadius;

    // Normalised against the tightest level, so level 0 always contributes at
    // unity and the wider levels stay relative to it however they are retuned.
    const base = p.weights.length > 0 && p.weights[0] > 0 ? p.weights[0] : 1;
    for (let i = 0; i < this.bloom.weights.length; i++) {
      this.bloom.weights[i] = (p.weights[i] ?? 0) / base;
    }

    // Each up step adds into a level that already holds its own band, so the
    // gain telescopes: L0 = B0 + w1(B1 + w2(B2 + ...)). The 13-tap downsample
    // preserves mean brightness, so a *large* bright region fills every level
    // and collects the whole sum-of-coefficients (2.74 at the default weights)
    // while a point emitter fills only the tightest levels and collects ~1.
    // A swarm therefore got three times the bloom gain of a lone enemy, which
    // is what lifted the background off black under load. Divide it back out so
    // bloom strength is a property of brightness, not of area.
    let telescoped = 1;
    for (let i = this.bloom.levels - 1; i >= 1; i--) {
      telescoped = 1 + this.bloom.weights[i] * telescoped;
    }
    const bloomGain = p.intensity / Math.max(1, telescoped);

    this.halation.radius = p.halationRadius;

    // `persistence` itself is not a uniform: it scales the seconds handed to
    // the pass in `decaySeconds`, so the slider reads as trail *length*.
    this.persistence.trailSaturation = p.trailSaturation;
    this.persistence.floor = p.persistenceFloor;
    this.persistence.setTau(p.phosphorTau[0], p.phosphorTau[1], p.phosphorTau[2]);
    this.persistence.setRate(p.phosphorRate[0], p.phosphorRate[1], p.phosphorRate[2]);

    const u = this.composite.uniforms;
    u.uBloomIntensity.value = bloomGain;
    u.uHalationStrength.value = p.halationStrength;
    (u.uHalationTint.value as THREE.Vector3).set(p.halationTint[0], p.halationTint[1], p.halationTint[2]);
    (u.uCurvature.value as THREE.Vector2).set(p.curvatureX, p.curvatureY);
    u.uOverscan.value = p.overscan;
    u.uChromaticAberration.value = p.chromaticAberration;
    u.uMaskPitch.value = Math.max(1, p.maskPitch);
    u.uMaskStrength.value = p.maskStrength;
    u.uScanlineStrength.value = p.scanlineStrength;
    u.uScanlineSharpness.value = Math.max(0.01, p.scanlineSharpness);
    u.uScanlineMinPeriod.value = Math.max(1, p.scanlineMinPeriod);
    // Goes through the setter rather than the uniform: the attenuation is a
    // function of the floor *and* of the last fit, and either can move alone.
    this.composite.scanlineFloor = Math.min(Math.max(p.scanlineFloor, 0), 1);
    u.uVignette.value = p.vignette;
    u.uVignettePower.value = Math.max(0.01, p.vignettePower);
    u.uVignetteDesat.value = p.vignetteDesat;
    u.uEdgeBlur.value = p.edgeBlur;
    u.uEdgeBlurStart.value = Math.min(p.edgeBlurStart, 0.999);
    u.uEdgeBlurPx.value = Math.max(0, p.edgeBlurPx);
    u.uNoise.value = p.noise;
    u.uGlare.value = p.glare;
  }
}
